import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Ruler, TrendingUp, Layers } from "lucide-react";
import type { Measurement } from "@/utils/estimateCalculations";

interface MeasurementsSummaryCardProps {
  measurement: Measurement;
}

export function MeasurementsSummaryCard({ measurement }: MeasurementsSummaryCardProps) {
  const squares = measurement.area_sqft ? (measurement.area_sqft / 100).toFixed(2) : "0";

  const totalLinearFeet =
    (measurement.eave_lf || 0) +
    (measurement.rake_lf || 0) +
    (measurement.ridge_lf || 0) +
    (measurement.hip_lf || 0) +
    (measurement.valley_lf || 0);

  const totalPenetrations =
    (measurement.pipe_count || 0) +
    (measurement.vent_count || 0) +
    (measurement.skylight_count || 0) +
    (measurement.chimney_count || 0) +
    (measurement.fan_count || 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Ruler className="h-5 w-5 text-primary" />
          Measurements Summary
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-1">
            <div className="text-sm text-muted-foreground">Roof Size</div>
            <div className="text-2xl font-bold">{squares} sq</div>
            <div className="text-xs text-muted-foreground">
              {measurement.area_sqft?.toLocaleString()} sq ft
            </div>
          </div>

          <div className="space-y-1">
            <div className="text-sm text-muted-foreground flex items-center gap-1">
              <TrendingUp className="h-3 w-3" />
              Pitch
            </div>
            <div className="text-2xl font-bold">
              {measurement.pitch || "N/A"}
            </div>
            <div className="text-xs text-muted-foreground">
              {measurement.layers || 1} layer{(measurement.layers || 1) > 1 ? "s" : ""}
            </div>
          </div>

          <div className="space-y-1">
            <div className="text-sm text-muted-foreground flex items-center gap-1">
              <Layers className="h-3 w-3" />
              Features
            </div>
            <div className="text-2xl font-bold">{totalPenetrations}</div>
            <div className="text-xs text-muted-foreground">penetrations</div>
          </div>
        </div>

        <div className="border-t pt-4">
          <div className="text-sm font-medium mb-2">Linear Footage Breakdown</div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            {measurement.eave_lf && measurement.eave_lf > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Eave:</span>
                <Badge variant="outline">{measurement.eave_lf.toFixed(1)} LF</Badge>
              </div>
            )}
            {measurement.rake_lf && measurement.rake_lf > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Rake:</span>
                <Badge variant="outline">{measurement.rake_lf.toFixed(1)} LF</Badge>
              </div>
            )}
            {measurement.ridge_lf && measurement.ridge_lf > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Ridge:</span>
                <Badge variant="outline">{measurement.ridge_lf.toFixed(1)} LF</Badge>
              </div>
            )}
            {measurement.hip_lf && measurement.hip_lf > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Hip:</span>
                <Badge variant="outline">{measurement.hip_lf.toFixed(1)} LF</Badge>
              </div>
            )}
            {measurement.valley_lf && measurement.valley_lf > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Valley:</span>
                <Badge variant="outline">{measurement.valley_lf.toFixed(1)} LF</Badge>
              </div>
            )}
            {measurement.flashing_lf && measurement.flashing_lf > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Flashing:</span>
                <Badge variant="outline">{measurement.flashing_lf.toFixed(1)} LF</Badge>
              </div>
            )}
          </div>
        </div>

        {totalPenetrations > 0 && (
          <div className="border-t pt-4">
            <div className="text-sm font-medium mb-2">Penetrations</div>
            <div className="flex flex-wrap gap-2">
              {measurement.skylight_count && measurement.skylight_count > 0 && (
                <Badge variant="secondary">
                  {measurement.skylight_count} Skylight{measurement.skylight_count > 1 ? "s" : ""}
                </Badge>
              )}
              {measurement.chimney_count && measurement.chimney_count > 0 && (
                <Badge variant="secondary">
                  {measurement.chimney_count} Chimney{measurement.chimney_count > 1 ? "s" : ""}
                </Badge>
              )}
              {measurement.pipe_count && measurement.pipe_count > 0 && (
                <Badge variant="secondary">
                  {measurement.pipe_count} Pipe{measurement.pipe_count > 1 ? "s" : ""}
                </Badge>
              )}
              {measurement.vent_count && measurement.vent_count > 0 && (
                <Badge variant="secondary">
                  {measurement.vent_count} Vent{measurement.vent_count > 1 ? "s" : ""}
                </Badge>
              )}
              {measurement.fan_count && measurement.fan_count > 0 && (
                <Badge variant="secondary">
                  {measurement.fan_count} Fan{measurement.fan_count > 1 ? "s" : ""}
                </Badge>
              )}
            </div>
          </div>
        )}

        <div className="text-xs text-muted-foreground">
          Total Linear Feet: <strong>{totalLinearFeet.toFixed(1)} LF</strong>
        </div>
      </CardContent>
    </Card>
  );
}
